import { useState, useEffect, useContext } from "react";
import { FiChevronDown, FiTrash2 } from "react-icons/fi";
import { MdModeEditOutline, MdCheck, MdClose } from "react-icons/md";
import { FaExternalLinkAlt } from "react-icons/fa";
import { SITE_FIELDS } from "./constants";
import { toProperCase } from "../../../Utils";
import { SiteContext } from "../../../Context/SiteContext";
import { useSites } from "../Hooks/useSites";
import {
  RoleValidator,
  Spinner,
  CompletionProgressBar,
} from "../../../Components/Common";
import { ROLES } from "../../../Constants/Strings";
import {
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
} from "@react-google-maps/api";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { MESSAGES, TOAST_MESSAGES } from "../Constants";
import { motion, AnimatePresence } from "framer-motion";

const containerStyle = { width: "100%", height: "100%" };
const center = { lat: 18.5204, lng: 73.8567 }; // Pune city center
const libraries = ["places"];

const SiteDetailsInfo = ({ siteId }) => {
  const { updateSite, loading, setLoading } = useSites({ siteId });
  const { site } = useContext(SiteContext);
  const [map, setMap] = useState(null);
  const [polygonData, setPolygonData] = useState(null);
  const [polygonPath, setPolygonPath] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isExpanded, setIsExpanded] = useState(true);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (!site?.geofence) return;

    try {
      let coordinates;

      if (typeof site?.geofence === "string") {
        const matches = site?.geofence.match(/\(\((.*?)\)\)/);
        if (matches && matches[1]) {
          const points = matches[1].split(",").map((pair) => {
            const [lng, lat] = pair.trim().split(" ").map(Number);
            return { lat, lng };
          });

          if (
            points.length > 1 &&
            points[0].lat === points[points.length - 1].lat &&
            points[0].lng === points[points.length - 1].lng
          ) {
            points.pop();
          }
          setPolygonPath(points);
        }
      } else if (typeof site?.geofence === "object") {
        coordinates = site?.geofence?.coordinates?.[0];
        if (coordinates) {
          const path = coordinates.map(([lng, lat]) => ({ lat, lng }));
          if (
            path.length > 1 &&
            path[0].lat === path[path.length - 1].lat &&
            path[0].lng === path[path.length - 1].lng
          ) {
            path.pop();
          }
          setPolygonPath(path);
        }
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || MESSAGES.GEOFENCE_ERROR;
      toast.error(msg);
    }
  }, [site?.geofence]);

  useEffect(() => {
    if (map && polygonPath.length > 0) {
      const bounds = new window.google.maps.LatLngBounds(); // A google maps class that defines a rectangular bounding box
      polygonPath.forEach((point) => bounds.extend(point)); // Adds each co-ordinates on that box
      map.fitBounds(bounds, 50); // 50 padding given (in pixels)
    }
  }, [map, polygonPath, isEditing]);

  useEffect(() => {
    if (siteId) {
      setLoading(false);
    }
  }, [siteId]);

  const handleEditClick = () => {
    setEditForm(site || {});
    setIsEditing(true);
  };

  useEffect(() => {
    if (polygonPath.length > 2) {
      const geojson = {
        type: "Polygon",
        coordinates: [
          [
            ...polygonPath.map((p) => [p.lng, p.lat]),
            [polygonPath[0].lng, polygonPath[0].lat],
          ],
        ],
      };
      setPolygonData(JSON.stringify(geojson));
    }
  }, [polygonPath]);

  const handleSave = async () => {
    if (polygonPath.length < 3) {
      toast.error(TOAST_MESSAGES.POLYGON_PATH_ERROR);
      return;
    }
    const payload = {
      ...editForm,
      geofence: polygonData,
    };
    const updated = await updateSite(siteId, payload);
    if (updated) {
      setIsEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[150px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (!site) {
    return <div className="text-center text-gray-500 py-6">No site found.</div>;
  }

  return (
    <div className="relative overflow-visible z-10">
      {/* Header */}
      <div
        className="flex flex-wrap items-center justify-between my-2 cursor-pointer select-none px-3 gap-3"
        onClick={() => {
          setIsExpanded((prev) => {
            const next = !prev;
            if (!next) setIsEditing(false);
            return next;
          });
        }}
      >
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-primary leading-snug break-words w-[45%]">
            {site?.site_name || "Site Name"}
          </h3>

          <div className="w-[45%] min-w-[120px]">
            <CompletionProgressBar
              subtitle=""
              progress={site?.completion_percentage || 0}
              color="bg-blue-500"
            />
          </div>
        </div>

        <button className="text-gray-600 transition-transform duration-300 flex-shrink-0">
          <FiChevronDown
            size={24}
            className={`transform transition-transform duration-200 ${
              isExpanded ? "rotate-180" : "rotate-0"
            }`}
          />
        </button>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="site-details-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.25, 1] }}
            className="overflow-visible"
          >
            <div className="relative p-4 overflow-visible">
              {/* Edit Controls */}
              {isEditing ? (
                <div className="absolute top-2 right-3 z-1 flex gap-1">
                  <button onClick={handleSave} className="p-1">
                    <MdCheck size={22} className="text-green-500" />
                  </button>
                  <button onClick={() => setIsEditing(false)} className="p-1">
                    <MdClose size={22} className="text-red-500" />
                  </button>
                </div>
              ) : (
                <RoleValidator
                  allowedRoles={[
                    ROLES.MANAGING_DIRECTOR,
                    ROLES.HEAD_OFFICE,
                    ROLES.GOVERNMENT_OFFICER,
                    ROLES.PROJECT_MANAGER,
                  ]}
                  mode="disable"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    className="absolute top-2 right-3 text-blue-600 z-1"
                  >
                    <MdModeEditOutline size={20} />
                  </button>
                </RoleValidator>
              )}

              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-2 h-full min-h-[350px]">
                <div className="col-span-2 md:col-span-3 space-y-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 w-full">
                    {SITE_FIELDS.map((field) => (
                      <div key={field.key}>
                        <h4 className="text-sm text-gray-500">{field.label}</h4>
                        {isEditing ? (
                          field.disabled ? (
                            <p className="text-sm font-semibold text-gray-400">
                              {site?.[field.key] || "-"}
                            </p>
                          ) : field.type === "select" ? (
                            <select
                              className="text-sm font-semibold pl-0 pt-0"
                              value={editForm[field.key] || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  [field.key]: e.target.value,
                                })
                              }
                              style={{
                                border: "none",
                                borderBottom: "2px solid #CBD5E0",
                                outline: "none",
                                boxShadow: "none",
                                width: "fit-content",
                                minWidth: "120px",
                                maxWidth: "70%",
                              }}
                            >
                              {field.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {toProperCase(opt.label)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type || "text"}
                              className="text-sm font-semibold pl-0 pt-0 pb-1"
                              value={editForm[field.key] || ""}
                              disabled={field.disabled}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  [field.key]: e.target.value,
                                })
                              }
                              style={{
                                border: "none",
                                borderBottom: "2px solid #CBD5E0",
                                outline: "none",
                                boxShadow: "none",
                                width: "fit-content",
                                minWidth: "120px",
                                maxWidth: "70%",
                              }}
                            />
                          )
                        ) : field.type === "link" ? (
                          site?.[field.key] ? (
                            <a
                              href={site[field.key]}
                              target="_blank"
                              className="text-blue-600 flex gap-2 items-center text-sm"
                              rel="noopener noreferrer"
                            >
                              Go to Map <FaExternalLinkAlt size={12} />
                            </a>
                          ) : (
                            <p className="text-sm font-semibold text-gray-500">
                              -
                            </p>
                          )
                        ) : (
                          <p className="text-sm font-semibold">
                            {field.format
                              ? field.format(site?.[field.key])
                              : field.options
                              ? field.options.find(
                                  (o) => o.value === site?.[field.key]
                                )?.label
                              : site?.[field.key] || "-"}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-span-2 md:col-span-3 flex flex-col">
                  <h4 className="text-sm text-gray-500 mb-1">Geofence</h4>
                  <div className="flex-1 border rounded shadow overflow-hidden relative min-h-[300px]">
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={polygonPath[0] || center}
                        zoom={12}
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        options={{
                          scrollwheel: false,
                          draggableCursor: "crosshair",
                        }}
                        onClick={(e) => {
                          if (isEditing) {
                            const lat = e.latLng.lat();
                            const lng = e.latLng.lng();
                            setPolygonPath((prev) => [...prev, { lat, lng }]);
                          }
                        }}
                      >
                        {polygonPath.length > 0 && (
                          <Polygon
                            path={polygonPath}
                            options={{
                              fillColor: "#2196f3",
                              fillOpacity: 0.3,
                              strokeColor: "#2196f3",
                              strokeWeight: 2,
                              editable: false,
                            }}
                          />
                        )}

                        {polygonPath.map((point, idx) => (
                          <Marker
                            key={idx}
                            position={point}
                            draggable={false}
                            icon={{
                              path: "M 0,0 m -2,0 a 2,2 0 1,0 4,0 a 2,2 0 1,0 -4,0",
                              fillColor: "#ff0000",
                              fillOpacity: 1,
                              strokeColor: "#ffffff",
                              strokeWeight: 1,
                              scale: 2,
                            }}
                          />
                        ))}
                      </GoogleMap>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spinner message="Loading map..." />
                      </div>
                    )}

                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => {
                          setPolygonPath([]);
                          setPolygonData(null);
                        }}
                        className="absolute flex items-center gap-1 top-3 left-1/2 -translate-x-1/2 bg-white hover:bg-gray-200 text-gray-600 px-3 py-2 rounded border border-slate-600 z-10 text-xs font-semibold"
                      >
                        <FiTrash2 />
                        Clear Polygon
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SiteDetailsInfo;
